import { useState, useEffect } from "react";
import {
  fetchRegionsByCountry,
  fetchCountries,
  fetchStates,
  fetchCities,
} from "../features/msp/services/mspService";
import { Region, Country, State, City } from "../features/msp/AddMspForm/types";

export function useRegionList(selectedCountryId?: number) {
  const [regionList, setRegionList] = useState<Region[]>([]);
  const [isRegionLoading, setIsRegionLoading] = useState(false);
  const [regionLoadingError, setRegionLoadingError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!selectedCountryId) {
      setRegionList([]);
      return;
    }

    let isComponentMounted = true;
    setIsRegionLoading(true);

    fetchRegionsByCountry(selectedCountryId)
      .then((regions) => {
        if (isComponentMounted) {
          setRegionList(regions);
          setRegionLoadingError(null);
        }
      })
      .catch((error: any) => {
        if (isComponentMounted) {
          setRegionLoadingError(error.message || "Failed to fetch regions");
        }
      })
      .finally(() => {
        if (isComponentMounted) setIsRegionLoading(false);
      });

    return () => {
      isComponentMounted = false;
    };
  }, [selectedCountryId]);

  return { regionList, isRegionLoading, regionLoadingError };
}

export function useCountryList() {
  const [countryList, setCountryList] = useState<Country[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [countryLoadingError, setCountryLoadingError] = useState<string | null>(
    null
  );

  useEffect(() => {
    let isComponentMounted = true;
    setIsCountryLoading(true);
    fetchCountries()
      .then((countries) => {
        if (isComponentMounted) {
          setCountryList(countries);
          setCountryLoadingError(null);
        }
      })
      .catch((error: any) => {
        if (isComponentMounted) {
          setCountryLoadingError(error.message || "Failed to fetch countries");
        }
      })
      .finally(() => {
        if (isComponentMounted) setIsCountryLoading(false);
      });

    return () => {
      isComponentMounted = false;
    };
  }, []);

  return { countryList, isCountryLoading, countryLoadingError };
}

export function useStateList(selectedCountryId?: number) {
  const [stateList, setStateList] = useState<State[]>([]);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [stateLoadingError, setStateLoadingError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!selectedCountryId) {
      setStateList([]);
      return;
    }

    let isComponentMounted = true;
    setIsStateLoading(true);

    fetchStates(selectedCountryId)
      .then((states) => {
        if (isComponentMounted) {
          setStateList(states);
          setStateLoadingError(null);
        }
      })
      .catch((error: any) => {
        if (isComponentMounted) {
          setStateLoadingError(error.message || "Failed to fetch states");
        }
      })
      .finally(() => {
        if (isComponentMounted) setIsStateLoading(false);
      });

    return () => {
      isComponentMounted = false;
    };
  }, [selectedCountryId]);

  return { stateList, isStateLoading, stateLoadingError };
}

export function useCityList(selectedStateId?: number) {
  const [cityList, setCityList] = useState<City[]>([]);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [cityLoadingError, setCityLoadingError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedStateId) {
      setCityList([]);
      return;
    }

    let isComponentMounted = true;
    setIsCityLoading(true);

    fetchCities(selectedStateId)
      .then((cities) => {
        if (isComponentMounted) {
          setCityList(cities);
          setCityLoadingError(null);
        }
      })
      .catch((error: any) => {
        if (isComponentMounted) {
          setCityLoadingError(error.message || "Failed to fetch cities");
        }
      })
      .finally(() => {
        if (isComponentMounted) setIsCityLoading(false);
      });

    return () => {
      isComponentMounted = false;
    };
  }, [selectedStateId]);

  return { cityList, isCityLoading, cityLoadingError };
}
